import { action, computed, makeObservable, observable, reaction } from 'mobx';
import {
  isSameMonth,
  addMonths,
  differenceInCalendarMonths,
  addDays,
  startOfDay,
  endOfDay,
  subMonths,
  subWeeks,
  startOfMonth
} from 'date-fns';

import { FieldModel } from 'shared/models/form';
import { validateIntervalAndReshuffle } from 'shared/entities/common/utils';
import { DateFormat } from 'shared/entities/date';
import { ReactionsHandlerStore } from 'stores/reactionsHandlerStore';
import { OpenStateModel } from 'shared/models/openState';
import {
  DatePickerDateFormat,
  DatePickerKind,
  DateRangePickerValue,
  IDateRangePicker
} from 'shared/newEntities/components/DatePicker';

import { DateBaseModel, IntervalDateModel } from '../DateModel';
import { CalendarMonthModel } from '../CalendarMonthModel';
import {
  areDatesEqual,
  mergeTimeIntoDate,
  START_OF_DAY_TIME,
  END_OF_DAY_TIME
} from '../utils/dateTimeUtils';
import {
  isCalendarDayPresetActive,
  isSameDayDateOnlyPresetActive,
  isSnapshotPresetActive,
  PRESET_MONTH_MONTHS,
  PRESET_YEAR_MONTHS
} from '../utils/dateRangePresetUtils';
import { buildDateRangeTriggerLabel } from '../utils/formatDateRangeTriggerLabel';

export class DateRangePickerModel
  extends ReactionsHandlerStore
  implements IDateRangePicker
{
  readonly kind: DatePickerKind.range = DatePickerKind.range;
  readonly dateFormat: DatePickerDateFormat;

  readonly firstValue: FieldModel<DateBaseModel | null> =
    new FieldModel<DateBaseModel | null>(null);
  readonly secondValue: FieldModel<DateBaseModel | null> =
    new FieldModel<DateBaseModel | null>(null);

  readonly hoverableDate: FieldModel<DateBaseModel | null> =
    new FieldModel<DateBaseModel | null>(null);
  readonly initialValue: FieldModel<DateRangePickerValue> =
    new FieldModel<DateRangePickerValue>(null);
  readonly cb: FieldModel<(_value: DateRangePickerValue) => void>;

  readonly modalOpened: OpenStateModel = new OpenStateModel();

  readonly minDate: Date | null;
  readonly alwaysShowFullYear: boolean;

  firstMonthModel: CalendarMonthModel<DatePickerKind.range>;
  secondMonthModel: CalendarMonthModel<DatePickerKind.range>;

  constructor({
    value,
    onChange,
    dateFormat = DateFormat.dayMonthYear,
    minDate = null,
    alwaysShowFullYear = false
  }: {
    value: DateRangePickerValue;
    onChange: (value: DateRangePickerValue) => void;
    dateFormat?: DatePickerDateFormat;
    minDate?: Date | null;
    alwaysShowFullYear?: boolean;
  }) {
    super();

    this.minDate = minDate;
    this.alwaysShowFullYear = alwaysShowFullYear;
    this.cb = new FieldModel(onChange);
    this.dateFormat = dateFormat;
    this.initialize(value);

    makeObservable(this, {
      firstMonthModel: observable.ref,
      secondMonthModel: observable.ref,

      isWithTime: computed,
      isZeroInterval: computed,
      applyButtonDisabled: computed,
      initialValueLabel: computed,
      isEqual: computed,
      isTodayActive: computed,
      isYesterdayActive: computed,
      isPreviousWeekActive: computed,
      isPreviousMonthActive: computed,
      isPreviousYearActive: computed,
      isFirstMonthBackDisabled: computed,
      isSecondMonthBackDisabled: computed,

      handleCalendarDateClick: action.bound,
      handleCalendarDateHover: action.bound,
      moveFirstMonth: action.bound,
      moveSecondMonth: action.bound,
      handleChangeSecondValue: action.bound,
      handleChangeFirstValue: action.bound,
      handleClearFirstValue: action.bound,
      handleClearSecondValue: action.bound,
      apply: action.bound,
      reset: action.bound,
      showInterval: action.bound,
      handleChangeInterval: action.bound,
      chooseInterval: action.bound,
      chooseTodayInterval: action.bound,
      chooseYesterdayInterval: action.bound,
      choosePreviousWeekInterval: action.bound,
      choosePreviousMonthInterval: action.bound,
      choosePreviousYearInterval: action.bound
    });
  }

  get isWithTime(): boolean {
    return this.dateFormat === DateFormat.dayMonthYearTime;
  }

  get applyButtonDisabled(): boolean {
    return (
      !this.firstValue.value || !this.secondValue.value || this.isZeroInterval
    );
  }

  isDateDisabled(date: Date): boolean {
    if (!this.minDate) {
      return false;
    }

    return startOfDay(date) < startOfDay(this.minDate);
  }

  get isFirstMonthBackDisabled(): boolean {
    return this.isUnavailableMonth(this.firstMonthModel.monthDate);
  }

  get isSecondMonthBackDisabled(): boolean {
    return this.isUnavailableMonth(
      addMonths(this.secondMonthModel.monthDate, -1)
    );
  }

  private resolveDefaultFirstMonthDate(): Date {
    const now = new Date();

    if (!this.minDate || !this.isUnavailableMonth(now)) {
      return now;
    }

    return startOfMonth(this.minDate);
  }

  get isZeroInterval(): boolean {
    if (!this.isWithTime || !this.firstValue.value || !this.secondValue.value) {
      return false;
    }

    return (
      this.firstValue.value.date.getTime() >=
      this.secondValue.value.date.getTime()
    );
  }

  /**
   * Текущий день должен быть выбран с 00:00 до 23:59, чтобы пресет был активен
   */
  get isTodayActive(): boolean {
    if (this.isWithTime) {
      return isCalendarDayPresetActive(
        this.firstValue.value,
        this.secondValue.value,
        new Date(),
        true
      );
    }

    return isSameDayDateOnlyPresetActive(
      this.firstValue.value,
      this.secondValue.value,
      new Date(),
      (date) => this.createDateBaseModel(date)
    );
  }

  get isYesterdayActive(): boolean {
    const yesterday = addDays(new Date(), -1);

    if (this.isWithTime) {
      return isCalendarDayPresetActive(
        this.firstValue.value,
        this.secondValue.value,
        yesterday,
        true
      );
    }

    return isSameDayDateOnlyPresetActive(
      this.firstValue.value,
      this.secondValue.value,
      yesterday,
      (date) => this.createDateBaseModel(date)
    );
  }

  get isPreviousWeekActive(): boolean {
    return isSnapshotPresetActive(
      this.firstValue.value,
      this.secondValue.value,
      (date) => subWeeks(date, 1),
      (date) => this.createDateBaseModel(date),
      this.isWithTime
    );
  }

  get isPreviousMonthActive(): boolean {
    return isSnapshotPresetActive(
      this.firstValue.value,
      this.secondValue.value,
      (date) => subMonths(date, PRESET_MONTH_MONTHS),
      (date) => this.createDateBaseModel(date),
      this.isWithTime
    );
  }

  get isPreviousYearActive(): boolean {
    return isSnapshotPresetActive(
      this.firstValue.value,
      this.secondValue.value,
      (date) => subMonths(date, PRESET_YEAR_MONTHS),
      (date) => this.createDateBaseModel(date),
      this.isWithTime
    );
  }

  get isEqual(): boolean {
    if (
      !this.initialValue.value &&
      !this.firstValue.value &&
      !this.secondValue.value
    ) {
      return true;
    }

    if (
      !this.initialValue.value ||
      !this.firstValue.value ||
      !this.secondValue.value
    ) {
      return false;
    }

    const boundaries = this.getBoundaryDates();

    if (!boundaries) {
      return false;
    }

    return (
      areDatesEqual(
        this.initialValue.value[0],
        boundaries.start,
        this.dateFormat
      ) &&
      areDatesEqual(this.initialValue.value[1], boundaries.end, this.dateFormat)
    );
  }

  get initialValueLabel() {
    return buildDateRangeTriggerLabel(
      this.initialValue.value,
      this.dateFormat,
      new Date(),
      this.alwaysShowFullYear
    );
  }

  initializeReactions(): void {
    this.addReaction({
      key: 'opened',
      reaction: reaction(
        () => this.modalOpened.opened,
        (opened) => {
          if (!opened) {
            this.reset();
          }
        }
      )
    });
  }

  initialize(value: DateRangePickerValue): void {
    /*
     * Проверка начального значения: если даты в интервале идут не по порядку, изменяем
     * */
    const interval = value
      ? validateIntervalAndReshuffle(value[0], value[1])
      : null;
    this.initialValue.changeValue(interval);

    this.firstValue.changeValue(
      interval ? this.createDateBaseModel(interval[0]) : null
    );
    this.secondValue.changeValue(
      interval ? this.createDateBaseModel(interval[1]) : null
    );

    /*
     * Определение начальных месяцев
     * */
    if (this.firstValue.value) {
      this.firstMonthModel = CalendarMonthModel.fromDefaultParams({
        monthDate: this.firstValue.value.date,
        datePicker: this
      });

      if (this.secondValue.value) {
        /*
         * Если интервал располагает в одном и том же месяце, то второй месяц - следующий
         * */
        if (
          isSameMonth(this.firstValue.value.date, this.secondValue.value.date)
        ) {
          this.secondMonthModel = CalendarMonthModel.fromDefaultParams({
            monthDate: addMonths(this.firstValue.value.date, 1),
            datePicker: this
          });
        } else {
          /*
           * Если интервал располагает в разных месяцах, то второй месяц - месяц второй даты в интервале
           * */
          this.secondMonthModel = CalendarMonthModel.fromDefaultParams({
            monthDate: this.secondValue.value.date,
            datePicker: this
          });
        }
      } else {
        this.secondMonthModel = CalendarMonthModel.fromDefaultParams({
          monthDate: addMonths(this.firstValue.value.date, 1),
          datePicker: this
        });
      }
    } else {
      const firstMonthDate = this.resolveDefaultFirstMonthDate();

      this.firstMonthModel = CalendarMonthModel.fromDefaultParams({
        monthDate: firstMonthDate,
        datePicker: this
      });
      this.secondMonthModel = CalendarMonthModel.fromDefaultParams({
        monthDate: addMonths(firstMonthDate, 1),
        datePicker: this
      });
    }
  }

  showDateOnFirstCalendar(model: DateBaseModel): void {
    const diffInMothBetweenDateAndFirstMonth = differenceInCalendarMonths(
      this.firstMonthModel.monthDate,
      model.date
    );

    this.moveFirstMonth(-diffInMothBetweenDateAndFirstMonth);
  }

  showDateOnSecondCalendar(model: DateBaseModel): void {
    const diffInMothBetweenDateAndSecondMonth = differenceInCalendarMonths(
      this.secondMonthModel.monthDate,
      model.date
    );

    this.moveSecondMonth(-diffInMothBetweenDateAndSecondMonth);
  }

  handleChangeFirstValue(value: DateBaseModel): void {
    const model = value.getCopy();

    if (this.secondValue.value && model.date > this.secondValue.value.date) {
      this.firstValue.changeValue(this.secondValue.value);
      this.secondValue.changeValue(model);
    } else {
      this.firstValue.changeValue(model);
    }

    this.showInterval();
  }

  handleChangeSecondValue(value: DateBaseModel): void {
    const model = value.getCopy();

    if (this.firstValue.value && model.date < this.firstValue.value.date) {
      this.secondValue.changeValue(this.firstValue.value);
      this.firstValue.changeValue(model);
    } else {
      this.secondValue.changeValue(model);
    }

    this.showInterval();
  }

  handleClearFirstValue = (): void => {
    this.firstValue.changeValue(null);
    this.showInterval();
  };

  handleClearSecondValue = (): void => {
    this.secondValue.changeValue(null);
    this.showInterval();
  };

  handleChangeInterval(value1: DateBaseModel, value2: DateBaseModel): void {
    if (value2.date > value1.date) {
      this.firstValue.changeValue(value1);
      this.secondValue.changeValue(value2);
    } else {
      this.firstValue.changeValue(value2);
      this.secondValue.changeValue(value1);
    }

    this.showInterval();
    this.apply();
  }

  chooseInterval(value: DateRangePickerValue): void {
    if (!value) {
      return;
    }

    this.handleChangeInterval(
      this.createDateBaseModel(value[0]),
      this.createDateBaseModel(value[1])
    );
  }

  chooseTodayInterval(): void {
    const now = new Date();

    if (this.isWithTime) {
      this.handleChangeInterval(
        this.createDateBaseModel(startOfDay(now)),
        this.createDateBaseModel(endOfDay(now))
      );
      return;
    }

    const model = this.createDateBaseModel(now);
    this.handleChangeInterval(model, model.getCopy());
  }

  chooseYesterdayInterval(): void {
    const yesterday = addDays(new Date(), -1);

    if (this.isWithTime) {
      this.handleChangeInterval(
        this.createDateBaseModel(startOfDay(yesterday)),
        this.createDateBaseModel(endOfDay(yesterday))
      );
      return;
    }

    const model = this.createDateBaseModel(yesterday);
    this.handleChangeInterval(model, model.getCopy());
  }

  choosePreviousWeekInterval(): void {
    const end = new Date();

    this.applyPresetSnapshot(subWeeks(end, 1), end);
  }

  choosePreviousMonthInterval(): void {
    const end = new Date();

    this.applyPresetSnapshot(subMonths(end, PRESET_MONTH_MONTHS), end);
  }

  choosePreviousYearInterval(): void {
    const end = new Date();

    this.applyPresetSnapshot(subMonths(end, PRESET_YEAR_MONTHS), end);
  }

  /**
   * Отобразить интервал. Первое значение в первом месяце, второе либо также в первом, либо во втором
   */
  showInterval(): void {
    if (this.firstValue.value) {
      const diffInMonths = differenceInCalendarMonths(
        this.firstMonthModel.monthDate,
        this.firstValue.value.date
      );

      this.moveFirstMonth(-diffInMonths);
    }

    if (this.secondValue.value) {
      if (this.firstValue.value) {
        const diffInMonthsBetweenDaysInInterval = differenceInCalendarMonths(
          this.firstValue.value.date,
          this.secondValue.value.date
        );

        /*
         * Если интервал располагает в одном месяце, то выходим
         * */
        if (diffInMonthsBetweenDaysInInterval === 0) {
          return;
        }
      }

      const diffInMonths = differenceInCalendarMonths(
        this.secondMonthModel.monthDate,
        this.secondValue.value.date
      );

      this.moveSecondMonth(-diffInMonths);
    }
  }

  /**
   * Обработчик клика на дату в календаре
   * @param value
   */
  handleCalendarDateClick(value: IntervalDateModel): void {
    if (this.isDateDisabled(value.date)) {
      return;
    }

    const model = new DateBaseModel(value.date);
    this.hoverableDate.changeValue(null);

    if (this.firstValue.value) {
      if (!this.secondValue.value) {
        if (model.date > this.firstValue.value.date) {
          this.secondValue.changeValue(model);
        } else {
          this.secondValue.changeValue(this.firstValue.value);
          this.firstValue.changeValue(model);
        }
      } else {
        this.firstValue.changeValue(model);
        this.secondValue.changeValue(null);
        this.hoverableDate.changeValue(model);
      }
    } else {
      this.firstValue.changeValue(model);
      this.secondValue.changeValue(null);
      this.hoverableDate.changeValue(model);
    }

    this.applyCalendarDefaultTimes();

    if (value.isInCalendarMonth) {
      return;
    }

    /*
     * Если дата, которую выбрали, не находится в месяце(disabled-дата), где ее отображают,
     * тогда месяц, где ее отображают, перемещают, чтобы она была видна
     * */
    const isClickInFirstCalendar =
      differenceInCalendarMonths(
        this.firstMonthModel.monthDate,
        value.monthDate
      ) === 0;

    if (isClickInFirstCalendar) {
      this.showDateOnFirstCalendar(model);
    } else {
      this.showDateOnSecondCalendar(model);
    }
  }

  /**
   * Обработчик наведения на дату в календаре
   * @param value
   */
  handleCalendarDateHover(value: IntervalDateModel): void {
    if (this.firstValue.value && !this.secondValue.value) {
      this.hoverableDate.changeValue(this.createDateBaseModel(value.date));
    }
  }

  /**
   * Перемещение первого месяца
   * @param offset на сколько переместить
   */
  moveFirstMonth(offset: number): void {
    const newMonthDate = addMonths(this.firstMonthModel.monthDate, offset);

    if (this.isUnavailableMonth(newMonthDate)) {
      return;
    }

    this.firstMonthModel = CalendarMonthModel.fromDefaultParams({
      monthDate: newMonthDate,
      datePicker: this
    });

    const diffInMonths = differenceInCalendarMonths(
      this.secondMonthModel.monthDate,
      this.firstMonthModel.monthDate
    );

    /*
     * Первый месяц должен идти перед вторым, если после перемещения первого месяца, второй оказался сзади, делаем его следующим после первого
     * */
    if (diffInMonths < 1) {
      this.secondMonthModel = CalendarMonthModel.fromDefaultParams({
        monthDate: addMonths(this.firstMonthModel.monthDate, 1),
        datePicker: this
      });
    }
  }

  /**
   * Перемещение второго месяца
   * @param offset на сколько переместить
   */
  moveSecondMonth(offset: number): void {
    const newMonthDate = addMonths(this.secondMonthModel.monthDate, offset);

    if (this.isUnavailableMonth(newMonthDate)) {
      return;
    }

    this.secondMonthModel = CalendarMonthModel.fromDefaultParams({
      monthDate: newMonthDate,
      datePicker: this
    });

    const diffInMonths = differenceInCalendarMonths(
      this.secondMonthModel.monthDate,
      this.firstMonthModel.monthDate
    );

    /*
     * Первый месяц должен идти перед вторым, если после перемещения второго месяца, первый оказался спереди, делаем его предыдущим
     * */
    if (diffInMonths < 1) {
      this.firstMonthModel = CalendarMonthModel.fromDefaultParams({
        monthDate: addMonths(this.secondMonthModel.monthDate, -1),
        datePicker: this
      });
    }
  }

  moveFirstMonthForward = (): void => {
    this.moveFirstMonth(1);
  };

  moveFirstMonthBack = (): void => {
    if (this.isFirstMonthBackDisabled) {
      return;
    }

    this.moveFirstMonth(-1);
  };

  moveSecondMonthBack = (): void => {
    if (this.isSecondMonthBackDisabled) {
      return;
    }

    this.moveSecondMonth(-1);
  };

  moveSecondMonthForward = (): void => {
    this.moveSecondMonth(1);
  };

  apply(): BaseResponse<DateRangePickerValue> {
    const boundaries = this.getBoundaryDates();

    if (boundaries && !this.isZeroInterval) {
      const newInitialData: [Date, Date] = [boundaries.start, boundaries.end];
      this.initialValue.changeValue(newInitialData);
      this.cb.value(newInitialData);

      return {
        isError: false,
        data: newInitialData
      };
    }

    return {
      isError: true
    };
  }

  private isUnavailableMonth(monthDate: Date | number): boolean {
    if (!this.minDate) {
      return false;
    }

    return (
      differenceInCalendarMonths(monthDate, startOfMonth(this.minDate)) < 0
    );
  }

  reset(): DateRangePickerValue {
    this.initialize(this.initialValue.value);

    return this.initialValue.value;
  }

  private createDateBaseModel(date: Date): DateBaseModel {
    return new DateBaseModel(date, this.dateFormat);
  }

  private getBoundaryDates(): { start: Date; end: Date } | null {
    if (!this.firstValue.value || !this.secondValue.value) {
      return null;
    }

    return {
      start: new Date(this.firstValue.value.date),
      end: new Date(this.secondValue.value.date)
    };
  }

  private applyCalendarDefaultTimes(): void {
    if (!this.isWithTime) {
      return;
    }

    if (this.firstValue.value) {
      this.firstValue.changeValue(
        this.createDateBaseModel(
          mergeTimeIntoDate(this.firstValue.value.date, START_OF_DAY_TIME)
        )
      );
    }

    if (this.secondValue.value) {
      this.secondValue.changeValue(
        this.createDateBaseModel(
          mergeTimeIntoDate(this.secondValue.value.date, END_OF_DAY_TIME)
        )
      );
    }
  }

  private applyPresetSnapshot(start: Date, end: Date): void {
    this.firstValue.changeValue(this.createDateBaseModel(start));
    this.secondValue.changeValue(this.createDateBaseModel(end));

    this.showInterval();
    this.apply();
  }
}
